/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { retryWhenOnline } from '@c-cdk/common';
import { Subject, finalize, map, mergeMap, of, takeUntil, tap } from 'rxjs';
import { SnackbarService } from '~backoffice/app/~shared/services/snackbar.service';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { ResourceFacade } from '~backoffice/app/~store/facades/resource.facade';
import {
  KalturaVideo,
  KalturaVideoStatus,
  TagAlgorithm,
  Video
} from '~core/models/core.models';
import { BadgeService } from '~core/services/badge.service';
import { TagService } from '~core/services/tag.service';
import { VideoModule } from '../../video.module';
import { VideoThumbnailSelectorComponent } from '../../components/video-thumbnail-selector/video-thumbnail-selector.component';
import { ProgramService } from '~core/services/program.service';
import { ContentService } from '~core/services/content.service';
import {
  MediaSourceStatus,
  MediaSourceType
} from '~core/models/media-source.models';
import { ContentType, CreateContentParams } from '~core/models/content.models';
import { removeNilProperties } from '~backoffice/app/~shared/utils/utils';

@Component({
  selector: 'edit-video',
  templateUrl: './edit-video.component.html',
  styleUrls: ['./edit-video.component.scss'],
  imports: [VideoModule, VideoThumbnailSelectorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditVideoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() video: Video | null | undefined;
  @Input() media: KalturaVideo | null | undefined;

  thumbnailControl = new FormControl<File | string | null>(null);
  form: FormGroup = new FormGroup({
    programId: new FormControl<string | null>(null, [Validators.required]),
    tagIds: new FormControl<string[] | null>(null, [Validators.required]),
    badgeIds: new FormControl<string[] | null>(null),
    name: new FormControl<string | null>(null, [Validators.required]),
    kalturaVideoId: new FormControl<string | null>(null, [Validators.required]),
    description: new FormControl<string | null>(null, [
      Validators.minLength(5),
      Validators.required
    ]),
    markedAsTeaser: new FormControl<boolean>(false)
    // tagMarkedAsReplay: new FormControl<string | null>(null),
  });

  tags$ = this.tagService
    .list({ itemPerPage: 1000_000 }) // algorithmId: TagAlgorithm.default
    .pipe(
      retryWhenOnline(),
      map(res =>
        res.items.filter(
          el =>
            el.algorithmId !== TagAlgorithm.currentlyLive &&
            el.algorithmId !== TagAlgorithm.myPrograms &&
            el.algorithmId !== TagAlgorithm.recommendedForYou
        )
      ),
      tap(items => {
        const defaultTag = items.find(el => el.markedAsDefault);
        if (defaultTag && !this.video?.tags.length) {
          this.form.patchValue({ tags: [defaultTag.id] });
        }
      })
    );

  badges$ = this.badgeService
    .list({ itemPerPage: 1000_000, published: true })
    .pipe(
      retryWhenOnline(),
      map(res => res.items)
    );

  tvShows$ = this.programService
    .list({ itemPerPage: 1000_000, published: true })
    .pipe(
      retryWhenOnline(),
      map(res => res.items)
    );

  isLoading = false;
  isLoadingWithPublication = false;
  isLoadingImport = false;
  isLoadingImportWithPublication = false;

  violations$ = this.globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
          this.changeDetection.detectChanges();
        }, 500);
      }
    })
  );

  kalturaVideoStatus = KalturaVideoStatus;

  private _destroy$ = new Subject<void>();
  constructor(
    private globalFacade: GlobalFacade,
    private contentService: ContentService,
    private tagService: TagService,
    private programService: ProgramService,
    private badgeService: BadgeService,
    public dialogRef: MatDialogRef<EditVideoComponent>,
    private snackbarService: SnackbarService,
    private resourceFacade: ResourceFacade,
    private changeDetection: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.patchFormValues(this.video, this.media);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.patchFormValues(
      changes['video']?.currentValue,
      changes['media']?.currentValue
    );
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  patchFormValues(
    video: Video | null | undefined,
    media: KalturaVideo | null | undefined
  ): void {
    if (video) {
      this.form.patchValue({
        ...video,
        kalturaVideoId: video.mediaSource.id,
        name: video.title,
        programId: video.program?.id,
        tagIds: video.tags?.map(el => el.id),
        badgeIds: video.badges?.map(el => el.id)
      });
      return;
    }

    if (media) {
      this.form.patchValue({
        kalturaVideoId: media.id,
        name: media.name,
        description: media.description,
        published: false
      });
      return;
    }
  }

  selectThumbnail(thumbnail: string | File): void {
    this.thumbnailControl.patchValue(thumbnail);
  }

  publishMedia(): void {
    this.isLoading = true;
    this.form.disable();
    let data = {
      ...this.form.value,
      ...(this.thumbnailControl.value && {
        thumbnail: this.thumbnailControl.value
      })
    };

    if (this.video) {
      data = {
        ...this.form.value,
        title: this.form.value.name,
        ...(this.thumbnailControl.value && {
          thumbnail: this.thumbnailControl.value
        })
      };
      delete data.name;
    }

    this.contentService
      .create(data)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this.form.enable();
          this.changeDetection.detectChanges();
        }),
        tap({
          next: () => {
            this.resourceFacade.getData({ refresh: true });
            this.snackbarService.open({
              text: 'Contenue importée',
              type: 'success'
            });
            this.dialogRef.close(true);
          }
        })
      )
      .subscribe();
  }

  importMedia(shouldBePublished = true): void {
    if (!this.media) return;

    if (shouldBePublished) {
      this.isLoadingImportWithPublication = true;
    } else {
      this.isLoadingImport = true;
    }

    this.form.disable();
    const { markedAsTeaser, kalturaVideoId, name, ...formValues } =
      this.form.value;
    const values = removeNilProperties({
      ...formValues,
      title: name,
      ...(shouldBePublished &&
        this.media.status === KalturaVideoStatus.READY && { published: true }),
      ...(shouldBePublished &&
        this.media.status !== KalturaVideoStatus.READY && {
          scheduledPublication: true
        }),
      mediaSource: {
        id: this.media.id,
        status:
          this.media.status === KalturaVideoStatus.READY
            ? MediaSourceStatus.ready
            : MediaSourceStatus.pending,
        type: MediaSourceType.kalturaMediaEntry
      },
      type: ContentType.video
    }) as CreateContentParams;

    this.contentService
      .create(values)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          if (shouldBePublished) {
            this.isLoadingImportWithPublication = false;
          } else {
            this.isLoadingImport = false;
          }
          this.form.enable();
          this.changeDetection.detectChanges();
        }),
        tap({
          next: () => {
            this.resourceFacade.getData({ refresh: true });
            this.snackbarService.open({
              text: 'Vidéo importée',
              type: 'success'
            });
            this.dialogRef.close(true);
          }
        })
      )
      .subscribe();
  }

  updateVideo(shouldBePublished = false): void {
    if (!this.video) return;

    if (shouldBePublished) {
      this.isLoadingWithPublication = true;
    } else {
      this.isLoading = true;
    }

    this.form.disable();
    const { kalturaVideoId, badgeIds, ...data } = this.form.value;
    const values = {
      ...data,
      title: this.form.value.name,
      ...(badgeIds && badgeIds.length && { badgeIds }),
      ...(this.thumbnailControl.value && {
        thumbnail: this.thumbnailControl.value
      })
    };
    delete values.name;
    delete values.markedAsTeaser;

    this.contentService
      .update(this.video.id, values)
      .pipe(
        takeUntil(this._destroy$),
        mergeMap(video => {
          const oldBadgeIds = this.video!.badges.map(badge => badge.id);
          if (oldBadgeIds.length && !badgeIds?.length) {
            return this.contentService
              .deleteBadges(this.video!.id, oldBadgeIds)
              .pipe(
                retryWhenOnline({ count: 1, delay: 3000 }),
                map(() => of(video))
              );
          }

          return of(video);
        }),
        finalize(() => {
          if (shouldBePublished) {
            this.isLoadingWithPublication = false;
          } else {
            this.isLoading = false;
          }
          this.form.enable();
          this.changeDetection.detectChanges();
        }),
        tap({
          next: () => {
            this.resourceFacade.getData({ refresh: true });
            this.snackbarService.open({
              text: values.scheduledPublication
                ? `Vidéo mise à jour (La publication sera effective une fois la vidéo importée)`
                : 'Vidéo mise à jour',
              type: 'success'
            });
            this.dialogRef.close(true);
          }
        })
      )
      .subscribe();
  }

  get isMedia(): boolean {
    return !!this.media;
  }

  get canPublishMedia(): boolean {
    const controlKeys = Object.keys(this.form.controls);
    return controlKeys.every(
      key =>
        !this.form.get(key)?.hasError('minLength') &&
        !this.form.get(key)?.hasError('min')
    );
  }

  get canPublishVideo(): boolean {
    const { name, program } = this.form.value;
    return !!this.video && !!name && !!program && !this.video.published;
  }

  get formArrayGroup(): FormGroup {
    const formArrayInstance = this.form.get('videos') as FormArray;
    return formArrayInstance.at(0) as FormGroup;
  }

  get modalTitle(): string {
    return this.isMedia
      ? `Configuration de la vidéo`
      : `Modification de la vidéo`;
  }

  get modalDescription(): string {
    return this.isMedia
      ? `Veuillez remplir les champs ci-dessous pour l'importation d'une nouvelle vidéo`
      : `Veuillez éditer les champs ci-dessous pour mettre à jour la vidéo`;
  }

  get isFormValid(): boolean {
    return (this.video && this.video.tvShow) ||
      (this.media && this.media.thumbnailUrl)
      ? !!this.form.valid
      : !!this.form.valid && !!this.thumbnailControl.value;
  }
}
